/**
 * The converter's wasm ABI v1. A fresh instance serves every call, so a trap
 * or a leak cannot reach the next one.
 */
const POINTER_SHIFT = BigInt(2 ** 32);
const LENGTH_BITS = 32;

interface MceIdlExports {
  memory: WebAssembly.Memory;
  mce_idl_abi_version(): number;
  mce_idl_alloc(len: number): number;
  mce_idl_free(ptr: number, len: number): void;
  mce_idl_call(ptr: number, len: number): bigint;
}

/** The module ran out of room or trapped: the host's HostTrap (7), never a verdict on the files. */
export class MceIdlHostTrap extends Error {
  constructor(detail: string) {
    super(`MCE IDL converter trap: ${detail}`);
    this.name = "MceIdlHostTrap";
  }
}

async function freshExports(module: WebAssembly.Module): Promise<MceIdlExports> {
  const instance = await WebAssembly.instantiate(module, {});
  return instance.exports as unknown as MceIdlExports;
}

/** The ABI version the module speaks. */
export async function mceIdlAbiVersion(module: WebAssembly.Module): Promise<number> {
  return (await freshExports(module)).mce_idl_abi_version();
}

/** Send one MCEI request and copy out the MCEI answer. */
export async function callMceIdl(
  module: WebAssembly.Module,
  request: Uint8Array,
): Promise<Uint8Array> {
  return (await callMceIdlMeasured(module, request)).answer;
}

/** The answer and the instance's memory afterwards, which only grows: its high-water mark. */
export async function callMceIdlMeasured(
  module: WebAssembly.Module,
  request: Uint8Array,
): Promise<{ answer: Uint8Array; memoryBytes: number }> {
  const wasm = await freshExports(module);
  try {
    const pointer = wasm.mce_idl_alloc(request.length);
    if (pointer === 0) throw new MceIdlHostTrap("no room for the request");
    new Uint8Array(wasm.memory.buffer, pointer, request.length).set(request);
    const packed = wasm.mce_idl_call(pointer, request.length);
    if (packed === BigInt(0)) throw new MceIdlHostTrap("no room for the answer");
    const outPointer = Number(packed / POINTER_SHIFT);
    const outLength = Number(BigInt.asUintN(LENGTH_BITS, packed));
    return {
      answer: new Uint8Array(wasm.memory.buffer, outPointer, outLength).slice(),
      memoryBytes: wasm.memory.buffer.byteLength,
    };
  } catch (error) {
    if (error instanceof MceIdlHostTrap) throw error;
    throw new MceIdlHostTrap(error instanceof Error ? error.message : String(error));
  }
}
